import { useCallback, useState } from "react";
import {
  DeletionImpact,
  useDeleteAccountMutation,
  useLazyGetAccountDeletionImpactQuery,
  useUpdateAccountStatusMutation,
} from "../../../../services";
import { notify } from "../../../../components/Notification";
import { getErrorMessages } from "../../../../services/errors";

export interface UseAccountActionsReturn {
  isDeleting: boolean;
  isUpdatingStatus: boolean;
  isLoadingImpact: boolean;
  /** Inline delete error; null when there is none. */
  deleteError: string | null;
  deletionImpact: DeletionImpact | null;
  handleDelete: () => Promise<void>;
  handleStatusChange: (currentStatus: boolean) => Promise<void>;
  fetchDeletionImpact: () => Promise<void>;
  /** Clears the inline delete error (call when reopening the modal). */
  resetDeleteError: () => void;
}

export interface UseAccountActionsOptions {
  accountId: number;
  onDeleteSuccess?: () => void;
  onStatusChangeSuccess?: () => void;
}

export const useAccountActions = ({
  accountId,
  onDeleteSuccess,
  onStatusChangeSuccess,
}: UseAccountActionsOptions): UseAccountActionsReturn => {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletionImpact, setDeletionImpact] = useState<DeletionImpact | null>(
    null,
  );

  const [deleteAccount, { isLoading: isDeleting }] = useDeleteAccountMutation();
  const [updateAccountStatus, { isLoading: isUpdatingStatus }] =
    useUpdateAccountStatusMutation();
  const [fetchImpact, { isFetching: isLoadingImpact }] =
    useLazyGetAccountDeletionImpactQuery();

  const fetchDeletionImpact = useCallback(async () => {
    setDeletionImpact(null);
    try {
      setDeletionImpact(await fetchImpact(accountId).unwrap());
    } catch {
      // Error toast handled by errorToastMiddleware; keep stale impact hidden.
      setDeletionImpact(null);
    }
  }, [fetchImpact, accountId]);

  const resetDeleteError = useCallback(() => setDeleteError(null), []);

  const handleDelete = useCallback(async () => {
    setDeleteError(null);
    try {
      await deleteAccount(accountId).unwrap();
      notify.success("Account deleted successfully");
      onDeleteSuccess?.();
    } catch (err) {
      // Keep the modal open with an inline error so the user can retry.
      setDeleteError(
        getErrorMessages(err as Parameters<typeof getErrorMessages>[0]).join(
          " ",
        ),
      );
    }
  }, [accountId, deleteAccount, onDeleteSuccess]);

  const handleStatusChange = useCallback(
    async (currentStatus: boolean) => {
      const newStatus = !currentStatus;
      const message = currentStatus
        ? "Account deactivated successfully"
        : "Account reactivated successfully";

      try {
        await updateAccountStatus({ accountId, isActive: newStatus }).unwrap();
        notify.success(message);
        onStatusChangeSuccess?.();
      } catch {
        // Error toast handled by errorToastMiddleware; stay on the page.
      }
    },
    [accountId, updateAccountStatus, onStatusChangeSuccess],
  );

  return {
    isDeleting,
    isUpdatingStatus,
    isLoadingImpact,
    deleteError,
    deletionImpact,
    handleDelete,
    handleStatusChange,
    fetchDeletionImpact,
    resetDeleteError,
  };
};
